/**
 * 设备状态管理状态仓库
 * 使用 Pinia 进行状态管理
 *
 * 职责:
 * - 管理 HID 设备的连接状态
 * - 缓存设备详情和状态数据
 * - 提供命令发送接口
 * - 处理轮询和事件监听
 */
import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import type { DeviceDetail, DeviceStatus, ConnectionType, DeviceCapabilities, VoiceType } from '@/api/device/types'
import { DEFAULT_DEVICE_CAPABILITIES, DEVICE_CAPABILITY_MAP } from '@/api/device/types'
import {
  buildSpeakerMuteCommand,
  buildMicMuteCommand,
  buildSpeakerVolumeCommand,
  buildMicVolumeCommand,
  buildEncCommand,
  buildRgbCommand,
  buildPowerCommand,
  buildAudioModeCommand,
  buildAudioBalanceCommand,
  buildSidetoneSwitchCommand,
  buildSidetoneVolumeCommand,
  buildEqLoginStatusCommand,
  buildBtShareSwitchCommand,
  buildCallBridgeSwitchCommand,
  buildVoiceEffectCommand,
} from '@/api/device/commands'

export const useDeviceStore = defineStore('device', () => {
  // 状态
  // isConnected: 仅表示 HID 通道已打开，不等于耳机链路已就绪
  const isConnected = ref(false)
  // isDeviceConnected: 业务链路是否真正连通（用于顶部状态展示/功能可用性）
  const isDeviceConnected = ref(false)
  const isConnecting = ref(false)
  const deviceDetail = ref<DeviceDetail | null>(null)
  const deviceStatus = ref<DeviceStatus | null>(null)
  const connectionType = ref<ConnectionType>(null)
  const error = ref<string | null>(null)
  const connectionStage = ref<string>('initial') // initial/requesting/opening/waiting_pairing/waiting_usb/connected/error
  const connectionMessage = ref<string | null>(null)
  const isRefreshing = ref(false) // 防止 refreshStatus 并发执行

  // 断线保护机制
  let deviceStatusMissCount = 0
  const MAX_STATUS_MISSES = 3
  let disconnectGuardTimer: NodeJS.Timeout | null = null
  const DISCONNECT_GUARD_MS = 200
  let statusPollingTimer: ReturnType<typeof setInterval> | null = null
  let statusPollingStarting = false
  let statusPollingSuspended = false
  const STATUS_POLL_INTERVAL_MS = 2000

  // 降噪设置（1-6档，对齐旧版 server-1.js）
  const noiseEnabled = ref(false)
  const noiseLevel = ref(1) // 默认1档，范围1-6
  const lastNoiseLevel = ref(1) // 记录关闭前的档位

  // 静音状态
  const speakerMuted = ref(false)
  const micMuted = ref(false)

  // 音量设置 (0-100)
  const speakerVolume = ref(50)
  const micVolume = ref(50)

  // RGB设置
  const rgbEnabled = ref(false)
  const rgbMode = ref(0)
  const rgbBrightness = ref(50)
  const lastRgbMode = ref(1)

  // 音频混合/分离
  const audioSplitEnabled = ref(false)
  const audioMode = ref<0 | 1 | 2>(0)
  const audioBalanceValue = ref(50) // 0-100, 50=中间
  const voiceEffect = ref<VoiceType>('normal')

  // 计算属性
  const deviceCategory = computed(() => {
    const fromDetail = deviceDetail.value?.deviceCategory
    if (fromDetail && fromDetail !== 'unknown') return fromDetail

    // 与旧项目保持一致：优先使用详情中的 deviceType，
    // 没有详情时使用状态中的 productType 分流（0=耳机，1=麦克风）。
    const pt = deviceStatus.value?.productType
    if (typeof pt === 'number') {
      if (pt === 1) return 'microPhone'
      return 'headPhone'
    }

    return fromDetail || 'unknown'
  })
  const isHeadPhone = computed(() => deviceCategory.value === 'headPhone')
  const isMicroPhone = computed(() => deviceCategory.value === 'microPhone')
  const batteryLevel = computed(() => deviceStatus.value?.batteryLevel ?? 0)
  // 对齐旧项目 globalVariable.js updateChargingStatus：batteryCharging===1 或 linkStatus===1 均显示充电图标
  const isCharging = computed(() => {
    const s = deviceStatus.value
    return !!(s && (s.batteryCharging === 1 || s.linkStatus === 1))
  })
  const isPowerOff = computed(() => (deviceStatus.value?.powerOffStatus || 0) === 1)

  // 设备能力配置（支持扩展新设备类型）
  const deviceCapabilities = computed<DeviceCapabilities>(() => {
    const category = deviceCategory.value
    const specificCaps = DEVICE_CAPABILITY_MAP[category] || {}
    return { ...DEFAULT_DEVICE_CAPABILITIES, ...specificCaps }
  })

  // 便捷的能力检查 getter
  const canUseEq = computed(() => deviceCapabilities.value.hasEq)
  const canUseNoiseReduction = computed(() => deviceCapabilities.value.hasNoiseReduction)
  const canUseSpecialAudio = computed(() => deviceCapabilities.value.hasSpecialAudio)
  const canUseLed = computed(() => deviceCapabilities.value.hasLed)
  const canUseHotkey = computed(() => deviceCapabilities.value.hasHotkey)
  const canUseLanguageSwitch = computed(() => deviceCapabilities.value.hasLanguageSwitch)
  const canUseFirmwareUpgrade = computed(() => deviceCapabilities.value.hasFirmwareUpgrade)
  const canUsePowerControl = computed(() => deviceCapabilities.value.hasPowerControl)
  const canUseVoiceChange = computed(() => deviceCapabilities.value.hasVoiceChange)

  // 操作

  function syncDeviceStatus(status: DeviceStatus): void {
    deviceStatus.value = status
    connectionType.value = hidManager.getConnectionType()

    // USB 与 2.4G 的麦克风/喇叭状态分别保存在不同字节，按当前链路选择。
    const useDongleState = connectionType.value === '2.4G'
    if (useDongleState && status.dongleMicVolumeLevel != null) {
      status.micVolumeLevel = status.dongleMicVolumeLevel
      status.micMute = status.dongleMicMute || 0
    } else if (status.usbMicVolumeLevel != null) {
      status.micVolumeLevel = status.usbMicVolumeLevel
      status.micMute = status.usbMicMute || 0
    }
    if (useDongleState && status.dongleSpeakerVolumeLevel != null) {
      status.speakerVolumeLevel = status.dongleSpeakerVolumeLevel
      status.speakerMute = status.dongleSpeakerMute || 0
    } else if (status.usbSpeakerVolumeLevel != null) {
      status.speakerVolumeLevel = status.usbSpeakerVolumeLevel
      status.speakerMute = status.usbSpeakerMute || 0
    }

    const isDongle = connectionType.value === '2.4G'
    const eqEnabled = isDongle ? status.dongleEqEnabled : status.usbEqEnabled
    const eqMode = isDongle ? status.dongleEqMode : status.usbEqMode
    const eqTag = isDongle ? status.dongleEqTag : status.usbEqTag
    void import('./eq').then(({ useEqStore }) => {
      useEqStore().syncHardwareStatus(
        Number(eqEnabled ?? status.eqEnabled ?? 0) === 1,
        Number(eqMode ?? status.eqMode ?? 0),
        Number(eqTag ?? status.eqTag ?? 0),
      )
    })
    void import('./chiji').then(({ useChijiStore }) => {
      const chijiEnabled = isDongle ? status.dongleChickenOneKey : status.usbChickenOneKey
      const chijiTag = isDongle ? status.dongleChickenTag : status.usbChickenTag
      useChijiStore().syncHardwareStatus({
        enabled: Number(chijiEnabled ?? ((isDongle ? status.chickenOneKey : status.chickenOneKey) & 0x80 ? 1 : 0)) === 1,
        tag: Number(chijiTag ?? ((isDongle ? status.chickenOneKey : status.chickenOneKey) & 0x7F)) || 0,
      })
    })

    noiseEnabled.value = (status.noiseReductionEnabled || 0) === 1
    noiseLevel.value = status.noiseReductionMode || 1
    if (!noiseEnabled.value) lastNoiseLevel.value = noiseLevel.value

    speakerMuted.value = (status.speakerMute || 0) !== 0
    micMuted.value = (status.micMute || 0) !== 0
    speakerVolume.value = Math.max(0, Math.min(100, status.speakerVolumeLevel || 0))
    micVolume.value = Math.max(0, Math.min(100, status.micVolumeLevel || 0))

    rgbMode.value = status.ledMode || 0
    rgbEnabled.value = rgbMode.value !== 0
    if (rgbMode.value !== 0) lastRgbMode.value = rgbMode.value

    const reportedAudioMode = Number(status.audioChannelMode || 0)
    audioMode.value = reportedAudioMode === 1 || reportedAudioMode === 2 ? reportedAudioMode : 0
    audioSplitEnabled.value = audioMode.value === 1
    audioBalanceValue.value = Math.max(0, Math.min(100, Math.round((status.mixVolume || 0) / 2)))

    // 0x80 里有固件版本，旧项目会合并到 deviceDetail 供固件升级页使用
    if (deviceDetail.value) {
      deviceDetail.value = {
        ...deviceDetail.value,
        softwareVersion: status.softwareVersion || deviceDetail.value.softwareVersion || '',
        bluetoothVersion: status.bluetoothVersion || deviceDetail.value.bluetoothVersion || '',
        noiseCancelVersion: status.noiseCancelVersion || deviceDetail.value.noiseCancelVersion || '',
        dongleVersion: status.dongleVersion || deviceDetail.value.dongleVersion || '',
      }
    }
  }

  async function connect(): Promise<boolean> {
    if (isConnecting.value) return false

    isConnecting.value = true
    error.value = null
    connectionStage.value = 'requesting'

    try {
      // 请求设备授权
      const success = await hidManager.connect()

      if (!success) {
        error.value = 'Failed to connect to device'
        connectionStage.value = 'error'
        return false
      }

      // 设备已打开，但业务链路需要等待状态确认
      isConnected.value = true
      connectionStage.value = 'opening'

      // 根据连接类型显示不同的等待提示
      if (connectionType.value === '2.4G') {
        connectionStage.value = 'waiting_pairing'
      } else if (connectionType.value === 'USB') {
        connectionStage.value = 'waiting_usb'
      }

      return true
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Connection error'
      connectionStage.value = 'error'
      return false
    } finally {
      isConnecting.value = false
    }
  }

  async function disconnect(): Promise<void> {
    stopStatusPolling()
    await hidManager.disconnect()
  }

  // 轮询状态：检测设备真实连接状态，处理断线
  async function refreshStatus(): Promise<void> {
    if (statusPollingSuspended || !isConnected.value || isRefreshing.value) return

    isRefreshing.value = true
    try {
      const status = await hidManager.getDeviceStatus()

      // OTA 暂停轮询后，之前已经发出的请求仍可能异步返回，此时必须丢弃结果。
      if (statusPollingSuspended) return

      if (!status) {
        // 状态查询失败
        deviceStatusMissCount++
        console.warn(`设备状态查询无返回: ${deviceStatusMissCount}/${MAX_STATUS_MISSES}`)

        if (deviceStatusMissCount >= MAX_STATUS_MISSES) {
          // 连续失败，判定设备断开
          handleDeviceDisconnected('status_timeout')
        }
        return
      }

      // 查询成功，重置失败计数
      deviceStatusMissCount = 0
      syncDeviceStatus(status)

      // 检查设备是否真正连接
      const connected = checkDeviceConnected(status)

      if (connected && !isDeviceConnected.value) {
        // 首次连接成功
        onDeviceConnectedSuccessfully(status)
      } else if (!connected && isDeviceConnected.value) {
        // 设备断开
        handleDeviceDisconnected('device_side')
      }
    } catch (error) {
      if (statusPollingSuspended) return
      console.error('轮询错误:', error)
      deviceStatusMissCount++
      if (deviceStatusMissCount >= MAX_STATUS_MISSES) {
        handleDeviceDisconnected('status_error')
      }
    } finally {
      isRefreshing.value = false
    }
  }

  /**
   * 检查设备是否真正连接。
   * 2.4G：依据 headsetConnected === 1 判断。
   * USB：依据 linkStatus === 1 判断。
   */
  function checkDeviceConnected(status: DeviceStatus): boolean {
    if (!status) return false

    if (connectionType.value === '2.4G') {
      return status.headsetConnected === 1
    } else if (connectionType.value === 'USB') {
      return status.linkStatus === 1
    }

    return false
  }

  /**
   * 设备首次连接成功处理
   */
  function onDeviceConnectedSuccessfully(status: DeviceStatus) {
    // 检查是否在断开保护期内
    if (disconnectGuardTimer) {
      console.warn('断开保护期内忽略连接成功回包')
      return
    }

    console.log('设备连接成功')

    isDeviceConnected.value = true
    connectionStage.value = 'connected'
    deviceStatusMissCount = 0

    // 同步设备状态
    syncDeviceStatus(status)

    // 麦克风设备自动跳转到麦克风EQ页面
    if (deviceCategory.value === 'microPhone') {
      // 使用动态导入 router，避免循环依赖
      import('@/router').then(({ default: router }) => {
        const currentPath = router.currentRoute.value.path
        // 只在非特殊页面时跳转
        if (!['/login', '/register', '/upgrade', '/user'].includes(currentPath)) {
          router.push('/eq').catch(() => {
            // 忽略重复导航错误
          })
        }
      })
    }

    // 停止轮询（改为定期轮询即可，不需要频繁轮询）
    // 轮询间隔可以调整为更长（如5秒）
  }

  /**
   * 处理设备断开
   * 启动断开保护期，防止瞬时断开导致误判
   */
  function handleDeviceDisconnected(reason: string) {
    console.log('设备断开:', reason)

    // 启动断开保护期
    beginDisconnectGuard()

    isDeviceConnected.value = false
    connectionStage.value = 'waiting_pairing'
    deviceStatusMissCount = 0

    // 保持轮询，等待重新连接
  }

  /**
   * 启动断开保护期
   * 200ms 内的状态变化会被忽略，防止瞬时断开
   */
  function beginDisconnectGuard() {
    if (disconnectGuardTimer) {
      clearTimeout(disconnectGuardTimer)
    }

    disconnectGuardTimer = setTimeout(() => {
      disconnectGuardTimer = null
    }, DISCONNECT_GUARD_MS)
  }

  function normalizeConnectionStage(stage: string): string {
    const aliases: Record<string, string> = {
      authorized_found: 'requesting',
      requesting_authorization: 'requesting',
      opening_device: 'opening',
      waiting_for_pairing: 'waiting_pairing',
      waiting_for_usb_connection: 'waiting_usb',
      upgrade_mode: 'error',
    }
    return aliases[stage] || stage
  }

  // 初始化自动检测，并在启动时尝试自动连接
  async function initAutoDetection(): Promise<boolean> {
    // 只绑定一次事件监听
    hidManager.on('device-connected', (detail: DeviceDetail) => {
      console.log('[DeviceStore] 设备连接事件:', detail.deviceName)
      deviceDetail.value = detail
      isConnected.value = true
      // 不在 0x82 详情返回时进入主界面；必须等待 0x80 状态确认业务连接
      error.value = null
    })

    hidManager.on('status-updated', (status: DeviceStatus) => {
      syncDeviceStatus(status)
      if (!statusPollingSuspended) void startStatusPolling()
    })

    hidManager.on('device-disconnected', () => {
      console.log('[DeviceStore] 设备断开事件')
      isConnected.value = false
      isDeviceConnected.value = false
      deviceDetail.value = null
      deviceStatus.value = null
      connectionType.value = null
      error.value = null
      stopStatusPolling()
    })

    // data-received 不再立即触发 refreshStatus，避免与连接流程冲突；
    // 状态由定时轮询负责读取。

    // connection-stage 是底层 HID 状态机对 UI 的桥接层
    hidManager.on('connection-stage', payload => {
      console.log('[DeviceStore] 连接阶段:', payload.stage, payload.message)
      let normalizedStage = normalizeConnectionStage(payload.stage)
      if (payload.stage === 'waiting_for_pairing' && payload.connectionType === 'USB') {
        normalizedStage = 'waiting_usb'
      }
      connectionStage.value = normalizedStage
      connectionMessage.value = payload.message || null
      if (payload.stage === 'upgrade_mode') {
        error.value = payload.message || '设备处于升级模式，请进入固件升级页面'
      }
      if (payload.connectionType) {
        connectionType.value = payload.connectionType
      }
      // 打开设备后就进入主界面容器（不再显示等待遮罩）
      if (payload.stage === 'opening_device' || payload.stage === 'waiting_for_pairing') {
        isConnected.value = true
      }
      // 业务链路确认成功后，再标记为完全连接
      if (payload.stage === 'connected') {
        isConnected.value = true
        isDeviceConnected.value = true
        void startStatusPolling()
      }
      if (payload.stage === 'disconnected') {
        isConnected.value = false
        isDeviceConnected.value = false
        stopStatusPolling()
      }
    })

    hidManager.on('error', (err: Error) => {
      if (statusPollingSuspended) return
      console.log('[DeviceStore] 错误:', err.message)
      error.value = err.message
    })

    const autoConnected = await hidManager.init()

    // 仅在 device-connected 事件后启动轮询，避免与连接初始化流程冲突。

    return autoConnected
  }

  async function startStatusPolling(): Promise<void> {
    if (statusPollingSuspended || statusPollingTimer || statusPollingStarting) return
    statusPollingStarting = true
    // 首次立即刷新，之后按旧项目 2 秒间隔持续回读 0x80。
    try {
      await refreshStatus()
      if (statusPollingSuspended || !isConnected.value || statusPollingTimer) return
      statusPollingTimer = setInterval(() => {
        void refreshStatus()
      }, STATUS_POLL_INTERVAL_MS)
    } finally {
      statusPollingStarting = false
    }
  }

  function stopStatusPolling(): void {
    if (!statusPollingTimer) return
    clearInterval(statusPollingTimer)
    statusPollingTimer = null
  }

  /** OTA 会话占用 HID 通道期间，暂停后台 0x80 状态轮询。 */
  async function suspendStatusPolling(): Promise<void> {
    statusPollingSuspended = true
    stopStatusPolling()

    // 如果之前的状态请求仍在等待响应，不要立即启动 OTA，避免两个流程同时占用通道。
    const deadline = Date.now() + 5500
    while (isRefreshing.value && Date.now() < deadline) {
      await new Promise(resolve => setTimeout(resolve, 50))
    }
  }

  function resumeStatusPolling(): void {
    statusPollingSuspended = false
    if (isConnected.value) void startStatusPolling()
  }

  // 降噪设置方法
  // ============ 命令发送操作 ============

  /**
   * 发送原始数据到设备
   */
  async function sendRawData(data: Uint8Array | string, waitForResponse = false): Promise<Uint8Array | void> {
    if (!isConnected.value) {
      throw new Error('Device not connected')
    }
    return hidManager.sendData(data, waitForResponse)
  }

  /**
   * 设置喇叭静音
   */
  async function setSpeakerMute(mute: boolean): Promise<void> {
    speakerMuted.value = mute
    const command = buildSpeakerMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置麦克风静音
   */
  async function setMicMute(mute: boolean): Promise<void> {
    micMuted.value = mute
    const command = buildMicMuteCommand(mute)
    await sendRawData(command, false)
  }

  /**
   * 设置喇叭音量 (0-100)
   */
  async function setSpeakerVolume(volume: number): Promise<void> {
    const prev = speakerVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    speakerVolume.value = clampedVolume
    const command = buildSpeakerVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !speakerMuted.value) {
      await setSpeakerMute(true)
    } else if (clampedVolume > 0 && prev === 0 && speakerMuted.value) {
      await setSpeakerMute(false)
    }
  }

  /**
   * 设置麦克风音量 (0-100)
   */
  async function setMicVolume(volume: number): Promise<void> {
    const prev = micVolume.value
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    micVolume.value = clampedVolume
    const command = buildMicVolumeCommand(clampedVolume)
    await sendRawData(command, false)

    // 对齐旧版: 麦克风音量滑到0自动静音；从0增大自动取消静音
    if (clampedVolume === 0 && !micMuted.value) {
      await setMicMute(true)
    } else if (clampedVolume > 0 && prev === 0 && micMuted.value) {
      await setMicMute(false)
    }
  }

  /**
   * 设置ENC降噪
   * 对齐旧版: 档位1-6，关闭时记录lastValue，开启时恢复
   */
  async function setNoiseEnabled(enabled: boolean, requestedLevel?: number): Promise<void> {
    if (enabled) {
      // 开启时恢复上次档位
      const restoreLevel = (requestedLevel ?? lastNoiseLevel.value) || 1
      noiseEnabled.value = true
      noiseLevel.value = restoreLevel
    } else {
      // 关闭时记录当前档位
      lastNoiseLevel.value = noiseLevel.value
      noiseEnabled.value = false
    }

    const command = buildEncCommand(noiseEnabled.value, noiseLevel.value)
    await sendRawData(command, false)
  }

  async function setNoiseLevel(level: number): Promise<void> {
    const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
    noiseLevel.value = clampedLevel

    // 如果当前降噪开启，发送命令；否则只更新值
    if (noiseEnabled.value) {
      const command = buildEncCommand(true, clampedLevel)
      await sendRawData(command, false)
    }
  }

  /**
   * 设置RGB灯效
   */
  async function setRgbEnabled(enabled: boolean): Promise<void> {
    if (!enabled) {
      if (rgbMode.value !== 0) {
        lastRgbMode.value = rgbMode.value
      }
      rgbEnabled.value = false
      rgbMode.value = 0
      const command = buildRgbCommand(false, 0)
      await sendRawData(command, false)
      return
    }

    rgbEnabled.value = true
    if (rgbMode.value === 0) {
      rgbMode.value = lastRgbMode.value || 1
    }
    const command = buildRgbCommand(true, rgbMode.value)
    await sendRawData(command, false)
  }

  async function setRgbMode(mode: number): Promise<void> {
    rgbMode.value = mode
    if (rgbEnabled.value) {
      const command = buildRgbCommand(true, mode)
      await sendRawData(command, false)
    }
  }

  /**
   * 对齐旧版：仅 2.4G 模式支持关机/开机（命令 0x70）
   */
  async function setPowerOn(powerOn: boolean): Promise<void> {
    if (!canUsePowerControl.value) {
      return
    }
    const command = buildPowerCommand(powerOn)
    await sendRawData(command, false)
  }

  async function togglePower(): Promise<void> {
    // 旧版: powerOffStatus=1 表示软件关机
    await setPowerOn(isPowerOff.value)
  }

  /**
   * 设置音频模式 (Mix/Split/Adaptive)
   * @param mode - 音频模式值（具体值需要根据协议定义）
   */
  async function setAudioSplit(enabled: boolean): Promise<void> {
    await setAudioMode(enabled ? 1 : 0)
  }

  async function setAudioMode(mode: 0 | 1 | 2): Promise<void> {
    audioMode.value = mode
    audioSplitEnabled.value = mode === 1
    const command = buildAudioModeCommand(mode)
    await sendRawData(command, false)
    if (deviceStatus.value) deviceStatus.value.audioChannelMode = mode
  }

  /**
   * 设置音频平衡 (0-100)
   */
  async function setAudioBalance(value: number): Promise<void> {
    const clampedValue = Math.max(0, Math.min(100, Math.round(value)))
    audioBalanceValue.value = clampedValue
    const command = buildAudioBalanceCommand(clampedValue)
    await sendRawData(command, false)
  }

  /**
   * 设置耳返开关 (新增)
   */
  async function setSidetoneEnabled(enabled: boolean): Promise<void> {
    const command = buildSidetoneSwitchCommand(enabled)
    await sendRawData(command, false)
    // 发送后主动读取一次状态，以硬件最终状态为准
    await refreshStatus()
  }

  /**
   * 设置耳返音量 (新增)
   * @param volume - 音量值 0-100
   */
  async function setSidetoneVolume(volume: number): Promise<void> {
    const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
    const command = buildSidetoneVolumeCommand(clampedVolume)
    await sendRawData(command, false)
  }

  /** 切换蓝牙音频共享（旧协议：0x5B / 0x04）。 */
  async function setBtShare(enabled: boolean): Promise<void> {
    await sendRawData(buildBtShareSwitchCommand(enabled), false)
    if (deviceStatus.value) deviceStatus.value.btShareEnabled = enabled ? 1 : 0
  }

  /** 切换通话桥接（旧协议：0x5B / 0x05）。 */
  async function setCallBridge(enabled: boolean): Promise<void> {
    await sendRawData(buildCallBridgeSwitchCommand(enabled), false)
    if (deviceStatus.value) deviceStatus.value.callBridgeEnabled = enabled ? 1 : 0
  }

  async function setVoiceEffect(effect: VoiceType): Promise<void> {
    const effectMap: Record<VoiceType, number> = {
      normal: 0,
      boy: 1,
      girl: 2,
      child: 3,
      robot: 4,
      monster: 5,
    }
    voiceEffect.value = effect
    await sendRawData(buildVoiceEffectCommand(effectMap[effect]), false)
  }

  /**
   * 发送 EQ 登录状态 (新增)
   * @param isLoggedIn - 是否已登录
   */
  async function sendEqLoginStatus(isLoggedIn: boolean): Promise<void> {
    const command = buildEqLoginStatusCommand(isLoggedIn)
    await sendRawData(command, false)
  }

  // ============ 旧版兼容接口 ============

  /**
   * 兼容旧版: 切换喇叭静音状态
   */
  async function toggleSpeakerMute(): Promise<void> {
    await setSpeakerMute(!speakerMuted.value)
  }

  /**
   * 兼容旧版: 切换麦克风静音状态
   */
  async function toggleMicMute(): Promise<void> {
    await setMicMute(!micMuted.value)
  }

  /** 兼容 AI 助手：喇叭静音 */
  async function setMute(mute: boolean): Promise<void> {
    await setSpeakerMute(mute)
  }

  return {
    // 状态
    isConnected,
    isDeviceConnected,
    isConnecting,
    deviceDetail,
    deviceStatus,
    connectionType,
    connectionStage,
    connectionMessage,
    error,
    noiseEnabled,
    noiseLevel,
    lastNoiseLevel,
    speakerMuted,
    micMuted,
    speakerVolume,
    micVolume,
    rgbEnabled,
    rgbMode,
    rgbBrightness,
    lastRgbMode,
    audioSplitEnabled,
    audioMode,
    audioBalanceValue,
    voiceEffect,
    // 计算属性
    deviceCategory,
    isHeadPhone,
    isMicroPhone,
    batteryLevel,
    isCharging,
    isPowerOff,
    // 设备能力（支持扩展新设备类型）
    deviceCapabilities,
    canUseEq,
    canUseNoiseReduction,
    canUseSpecialAudio,
    canUseLed,
    canUseHotkey,
    canUseLanguageSwitch,
    canUseFirmwareUpgrade,
    canUsePowerControl,
    canUseVoiceChange,
    // 操作
    connect,
    disconnect,
    refreshStatus,
    initAutoDetection,
    startStatusPolling,
    stopStatusPolling,
    suspendStatusPolling,
    resumeStatusPolling,
    setNoiseEnabled,
    setNoiseLevel,
    setSpeakerMute,
    setMicMute,
    setSpeakerVolume,
    setMicVolume,
    setRgbEnabled,
    setRgbMode,
    togglePower,
    setPowerOn,
    setAudioSplit,
    setAudioMode,
    setAudioBalance,
    setSidetoneEnabled,
    setSidetoneVolume,
    setBtShare,
    setCallBridge,
    setVoiceEffect,
    sendEqLoginStatus,
    toggleSpeakerMute,
    toggleMicMute,
    setMute,
    sendRawData,
  }
})
